import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { RefreshCw, Search, TrendingDown, TrendingUp, Minus, Edit2, Check, X, Users, Plane, ArrowLeft } from 'lucide-react';
import API_URL from '../config';
import { useNavigate } from 'react-router-dom';

const API_BASE = `${API_URL}/api`;

const fmtDays = (n) => {
  const v = parseFloat(n);
  if (isNaN(v)) return '0';
  return parseFloat(v.toFixed(2)).toString();
};

const BalanceBar = ({ value, max = 30 }) => {
  const pct = Math.min(Math.max((parseFloat(value) / max) * 100, 0), 100);
  const isNeg = parseFloat(value) < 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div
        className={`h-2 rounded-full transition-all ${isNeg ? 'bg-red-400' : pct < 25 ? 'bg-orange-400' : 'bg-emerald-500'}`}
        style={{ width: `${isNeg ? 100 : pct}%` }}
      />
    </div>
  );
};

export default function SoldesConges() {
  const { isManager } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name | days_asc | days_desc
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!isManager) { navigate('/conges'); return; }
    load();
  }, [isManager, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [usersRes, balancesRes] = await Promise.all([
        fetch(`${API_URL}/users/`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/leaves/balances`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (balancesRes.ok) setBalances(await balancesRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Merge users (active only) with their balance
  const enriched = useMemo(() => {
    const activeUsers = users.filter(u => u.is_active !== false);
    return activeUsers.map(u => {
      const bal = balances.find(b => b.user_id === u.id);
      return {
        ...u,
        available_days: bal?.available_days ?? null,
        anticipated_days: bal?.anticipated_days ?? 0,
        balance_id: bal?.id ?? null,
        updated_at: bal?.updated_at ?? null,
      };
    });
  }, [users, balances]);

  const filtered = useMemo(() => {
    let list = enriched.filter(u =>
      (u.full_name || u.email).toLowerCase().includes(search.toLowerCase())
    );
    if (sortBy === 'days_asc') list = [...list].sort((a, b) => (a.available_days ?? Infinity) - (b.available_days ?? Infinity));
    if (sortBy === 'days_desc') list = [...list].sort((a, b) => (b.available_days ?? -Infinity) - (a.available_days ?? -Infinity));
    if (sortBy === 'name') list = [...list].sort((a, b) => (a.full_name || a.email).localeCompare(b.full_name || b.email));
    return list;
  }, [enriched, search, sortBy]);

  const stats = useMemo(() => {
    const withBalance = enriched.filter(u => u.available_days !== null);
    const negCount = withBalance.filter(u => u.available_days < 0).length;
    const lowCount = withBalance.filter(u => u.available_days >= 0 && u.available_days < 5).length;
    const avg = withBalance.length > 0 ? withBalance.reduce((s, u) => s + parseFloat(u.available_days), 0) / withBalance.length : 0;
    return { total: enriched.length, negCount, lowCount, avg };
  }, [enriched]);

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditValue(u.available_days !== null ? fmtDays(u.available_days) : '0');
    setSaveError(null);
  };

  const cancelEdit = () => { setEditingId(null); setEditValue(''); setSaveError(null); };

  const saveEdit = async (userId) => {
    const days = Math.round(parseFloat(editValue) * 100) / 100;
    if (isNaN(days)) { setSaveError('Valeur invalide'); return; }
    setSaving(true); setSaveError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/leaves/balance/${userId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ available_days: days }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Erreur'); }
      const updated = await res.json();
      setBalances(prev => {
        const idx = prev.findIndex(b => b.user_id === userId);
        if (idx >= 0) { const copy = [...prev]; copy[idx] = updated; return copy; }
        return [...prev, updated];
      });
      setEditingId(null);
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  const getInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';

  const avatarColors = [
    'bg-indigo-500','bg-purple-500','bg-emerald-500','bg-orange-500','bg-pink-500','bg-teal-500','bg-cyan-500',
  ];
  const avatarColor = (id) => avatarColors[id % avatarColors.length];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white hover:shadow-sm rounded-xl transition-all text-gray-500 flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="p-2 bg-indigo-100 rounded-xl">
              <Plane className="w-6 h-6 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-extrabold text-[#1A1B4B]">Soldes de Congés</h1>
          </div>
          <p className="text-gray-500 text-sm ml-1">Vue d'ensemble des soldes de l'équipe — employés actifs uniquement</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium">Employés actifs</div>
            <div className="text-2xl font-extrabold text-[#1A1B4B]">{stats.total}</div>
            <div className="text-xs text-gray-400">personnes</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Minus className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium">Moyenne équipe</div>
            <div className="text-2xl font-extrabold text-[#1A1B4B]">{fmtDays(stats.avg)}</div>
            <div className="text-xs text-gray-400">jours</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingDown className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <div className="text-xs text-red-500 font-medium">Soldes négatifs</div>
            <div className="text-2xl font-extrabold text-red-500">{stats.negCount}</div>
            <div className="text-xs text-red-400">anticipés</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <div className="text-xs text-orange-500 font-medium">Solde &lt; 5j</div>
            <div className="text-2xl font-extrabold text-orange-500">{stats.lowCount}</div>
            <div className="text-xs text-orange-400">à surveiller</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 shadow-sm"
          />
        </div>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:border-indigo-400 shadow-sm text-gray-700"
        >
          <option value="name">Trier par nom</option>
          <option value="days_asc">Solde croissant</option>
          <option value="days_desc">Solde décroissant</option>
        </select>
      </div>

      {/* Table / Cards */}
      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold">
            <tr className="border-b border-gray-200">
              <th className="py-4 pl-5 text-left">Employé</th>
              <th className="py-4 px-4 text-left">Rôle</th>
              <th className="py-4 px-4 text-left min-w-[180px]">Solde disponible</th>
              <th className="py-4 px-4 text-left">Anticipé</th>
              <th className="py-4 px-4 text-left">Mis à jour</th>
              <th className="py-4 pr-5 text-right">Modifier</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(u => {
              const isNeg = u.available_days !== null && parseFloat(u.available_days) < 0;
              const isLow = u.available_days !== null && parseFloat(u.available_days) >= 0 && parseFloat(u.available_days) < 5;
              const isEditing = editingId === u.id;
              return (
                <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="py-3.5 pl-5">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${avatarColor(u.id)}`}>
                        {getInitials(u.full_name || u.email)}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800 text-sm">{u.full_name || '—'}</div>
                        <div className="text-xs text-gray-400">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="text-xs font-medium px-2 py-1 rounded-lg bg-gray-100 text-gray-600">{u.role || '—'}</span>
                  </td>
                  <td className="py-3.5 px-4">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          step="0.5"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          className="w-28 px-2 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(u.id); if (e.key === 'Escape') cancelEdit(); }}
                          placeholder="ex: -5"
                        />
                        <span className="text-xs text-gray-400">jours</span>
                        <span className="text-[10px] text-gray-400 italic">(négatif = anticipé)</span>
                        {saveError && <span className="text-xs text-red-500">{saveError}</span>}
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-lg font-extrabold ${isNeg ? 'text-red-500' : isLow ? 'text-orange-500' : 'text-gray-800'}`}>
                            {u.available_days !== null ? fmtDays(u.available_days) : '—'}
                          </span>
                          <span className="text-xs text-gray-400">j</span>
                          {isNeg && <span className="text-[10px] font-bold bg-red-100 text-red-600 border border-red-200 rounded px-1.5 py-0.5">Anticipé</span>}
                          {isLow && !isNeg && <span className="text-[10px] font-bold bg-orange-100 text-orange-600 border border-orange-200 rounded px-1.5 py-0.5">Faible</span>}
                        </div>
                        {u.available_days !== null && <BalanceBar value={u.available_days} />}
                      </div>
                    )}
                  </td>
                  <td className="py-3.5 px-4">
                    <span className={`text-sm font-semibold ${parseFloat(u.anticipated_days) > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                      {parseFloat(u.anticipated_days) > 0 ? `${fmtDays(u.anticipated_days)} j` : '—'}
                    </span>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="text-xs text-gray-400">
                      {u.updated_at ? new Date(u.updated_at).toLocaleDateString('fr-FR') : '—'}
                    </span>
                  </td>
                  <td className="py-3.5 pr-5 text-right">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => saveEdit(u.id)} disabled={saving} className="p-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg transition-colors"><Check className="w-4 h-4" /></button>
                        <button onClick={cancelEdit} className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(u)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"><Edit2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="py-12 text-center text-gray-400 text-sm">Aucun employé trouvé.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3 pb-6">
        {filtered.length === 0 && <p className="py-8 text-center text-gray-400 text-sm">Aucun employé trouvé.</p>}
        {filtered.map(u => {
          const isNeg = u.available_days !== null && parseFloat(u.available_days) < 0;
          const isLow = u.available_days !== null && parseFloat(u.available_days) >= 0 && parseFloat(u.available_days) < 5;
          const isEditing = editingId === u.id;
          return (
            <div key={u.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${avatarColor(u.id)}`}>
                    {getInitials(u.full_name || u.email)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{u.full_name || '—'}</div>
                    <div className="text-xs text-gray-400">{u.role || '—'}</div>
                  </div>
                </div>
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <button onClick={() => saveEdit(u.id)} disabled={saving} className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg"><Check className="w-4 h-4" /></button>
                    <button onClick={cancelEdit} className="p-1.5 bg-gray-100 text-gray-600 rounded-lg"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => startEdit(u)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"><Edit2 className="w-4 h-4" /></button>
                )}
              </div>
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <input type="number" step="0.5" value={editValue} onChange={e => setEditValue(e.target.value)} className="flex-1 px-3 py-2 border border-indigo-300 rounded-xl text-sm focus:outline-none" autoFocus placeholder="ex: -5 (négatif = anticipé)" />
                  <span className="text-sm text-gray-400">jours</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-extrabold ${isNeg ? 'text-red-500' : isLow ? 'text-orange-500' : 'text-gray-800'}`}>
                        {u.available_days !== null ? fmtDays(u.available_days) : '—'}
                      </span>
                      <span className="text-sm text-gray-400">jours</span>
                      {isNeg && <span className="text-[10px] font-bold bg-red-100 text-red-600 border border-red-200 rounded px-1.5 py-0.5">Anticipé</span>}
                      {isLow && !isNeg && <span className="text-[10px] font-bold bg-orange-100 text-orange-600 border border-orange-200 rounded px-1.5 py-0.5">Faible</span>}
                    </div>
                    {parseFloat(u.anticipated_days) > 0 && (
                      <span className="text-xs text-orange-500 font-semibold">{fmtDays(u.anticipated_days)}j anticipé</span>
                    )}
                  </div>
                  {u.available_days !== null && <BalanceBar value={u.available_days} />}
                </>
              )}
              {saveError && editingId === u.id && <p className="text-xs text-red-500 mt-1">{saveError}</p>}
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
}
